import { IconButton } from '@affine/component';
import { DocumentLoadMaskProvider } from '@affine/core/blocksuite/block-suite-editor/document-load-mask';
import { AffineErrorBoundary } from '@affine/core/components/affine/affine-error-boundary';
import {
  mobileNavigationRailOpen$,
  toggleMobileNavigationRail,
} from '@affine/core/components/root-app-sidebar/mobile-navigation-rail';
import { setWorkspaceNavigationHost } from '@affine/core/components/root-app-sidebar/workspace-navigation-host';
import { workbenchRoutes } from '@affine/core/desktop/workbench-router';
import { useI18n } from '@affine/i18n';
import { RightSidebarIcon, SidebarIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import { Suspense, useCallback, useMemo, useState } from 'react';
import { matchRoutes, Outlet } from 'react-router-dom';

import { AFFINE_RIGHT_SIDEBAR_SHORTCUT } from '../../../commands/affine-keybindings';
import { AssistantFollowing } from '../../streamlain/copilot/assistant-follow-control';
import {
  RIGHT_SIDEBAR_NAVIGATION_OWNER,
  RIGHT_SIDEBAR_OPEN_ID,
} from '../../streamlain/copilot/right-sidebar-navigation';
import {
  UiNavigationItem,
  useUiNavigationDefinition,
} from '../../streamlain/copilot/ui-navigation-item';
import { WorkspaceViewContextService } from '../../streamlain/copilot/workspace-view-context';
import { ViewService } from '../services/view';
import { WorkbenchService } from '../services/workbench';
import * as styles from './route-container.css';
import { useDocumentNavigationMotion } from './use-document-navigation-motion';
import { useViewPosition } from './use-view-position';
import {
  ViewBody,
  ViewBodyTarget,
  ViewHeader,
  ViewHeaderTarget,
} from './view-islands';
import { ViewTitle } from './view-meta';

export interface Props {
  route: {
    Component: React.ComponentType;
  };
}

export const RightSidebarOpenButton = ({
  className,
  show,
}: {
  className: string;
  show: boolean;
}) => {
  const t = useI18n();
  const context = useService(WorkspaceViewContextService);
  const view = useService(ViewService).view;
  const scope = useMemo(
    () => ({ workspaceId: context.workspaceId, viewId: view.id }),
    [context, view]
  );
  const definition = useUiNavigationDefinition(
    scope,
    RIGHT_SIDEBAR_NAVIGATION_OWNER,
    RIGHT_SIDEBAR_OPEN_ID
  );
  const tooltip = show
    ? t['com.streamlain.cmdk.affine.right-sidebar.expand']()
    : t['com.streamlain.cmdk.affine.right-sidebar.collapse']();

  if (!definition) return null;
  return (
    <UiNavigationItem
      scope={scope}
      ownerId={RIGHT_SIDEBAR_NAVIGATION_OWNER}
      definition={definition}
    >
      <IconButton
        size="24"
        className={className}
        data-show={show}
        data-testid="right-sidebar-toggle"
        aria-hidden={!show}
        aria-label={tooltip}
        tabIndex={show ? undefined : -1}
        tooltip={tooltip}
        tooltipShortcut={AFFINE_RIGHT_SIDEBAR_SHORTCUT}
        tooltipOptions={{ side: 'bottom' }}
      >
        <RightSidebarIcon />
      </IconButton>
    </UiNavigationItem>
  );
};

/** The phone top bar's burger: shows and hides the app's navigation rail. */
const NavigationRailButton = ({ className }: { className: string }) => {
  const t = useI18n();
  const open = useLiveData(mobileNavigationRailOpen$);
  const label = open
    ? t['com.streamlain.cmdk.affine.left-sidebar.collapse']()
    : t['com.streamlain.cmdk.affine.left-sidebar.expand']();
  return (
    <IconButton
      size="24"
      className={className}
      aria-controls="global-navigation-rail"
      aria-expanded={open}
      aria-label={label}
      data-testid="navigation-rail-toggle"
      onClick={toggleMobileNavigationRail}
    >
      <SidebarIcon />
    </IconButton>
  );
};

const RoutePendingFallback = () => {
  const t = useI18n();
  const label = t['Loading']();

  return (
    <>
      <ViewTitle title={label} />
      <ViewHeader />
      <ViewBody>
        <div className={styles.routePending} role="status" aria-live="polite">
          <span className={styles.routePendingText}>{label}</span>
        </div>
      </ViewBody>
    </>
  );
};

export const RouteContainer = () => {
  const viewPosition = useViewPosition();
  const workbench = useService(WorkbenchService).workbench;
  const view = useService(ViewService).view;
  // Follow the requested location immediately, before a lazy route resolves.
  const location = useLiveData(view.location$);
  const match = matchRoutes(workbenchRoutes, location)?.at(-1);
  const documentId =
    match?.route.path === '/:pageId' ? match.params.pageId : undefined;
  const query = new URLSearchParams(location.search);
  const personId = query.get('presenceUserId');
  const personOpeningId = personId
    ? JSON.stringify([personId, query.get('refreshKey')])
    : undefined;
  const [documentStage, setDocumentStage] = useState<HTMLDivElement | null>(
    null
  );
  useDocumentNavigationMotion(documentStage, documentId, location.pathname);
  const documentStageRef = useCallback(
    (stage: HTMLDivElement | null) => {
      setDocumentStage(stage);
      view.contentElement$.next(stage);
    },
    [view]
  );
  const sidebarOpen = useLiveData(workbench.sidebarOpen$);
  const navigationHostRef = useCallback(
    (host: HTMLDivElement | null) => setWorkspaceNavigationHost(view.id, host),
    [view.id]
  );

  return (
    <div className={styles.root}>
      {viewPosition.isFirst ? <AssistantFollowing /> : null}
      <div className={styles.header}>
        {viewPosition.isFirst && (
          <NavigationRailButton className={styles.leftSidebarButton} />
        )}
        <ViewHeaderTarget
          viewId={view.id}
          className={styles.viewHeaderContainer}
        />
        {viewPosition.isLast && (
          <div className={styles.headerEnd}>
            {!BUILD_CONFIG.isElectron && (
              <RightSidebarOpenButton
                show={!sidebarOpen}
                className={styles.rightSidebarButton}
              />
            )}
          </div>
        )}
      </div>

      <DocumentLoadMaskProvider
        pageId={documentId}
        maskHost={documentStage}
        openingId={personOpeningId}
      >
        <AffineErrorBoundary fallbackHost={ViewBody}>
          <Suspense fallback={<RoutePendingFallback />}>
            <Outlet />
          </Suspense>
        </AffineErrorBoundary>
        <div ref={navigationHostRef} className={styles.viewBodyViewport}>
          <div
            ref={documentStageRef}
            className={styles.documentStage}
            data-workbench-body-content="true"
          >
            <ViewBodyTarget
              viewId={view.id}
              className={styles.viewBodyContainer}
            />
          </div>
        </div>
      </DocumentLoadMaskProvider>
    </div>
  );
};
